import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import "@/components/admin/DashboardHome.css";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  adminStats, crmStats, crmViewCounts, crmDeals, candidateStats,
  listCandidates, adminSubmissions, leadRecommendedCandidates,
} from "@/lib/api";
import {
  Target, Users, Briefcase, FileText, TrendingUp, DollarSign, Trophy, Percent,
  AlertTriangle, Clock, Sparkles, ChevronRight, CircleUser, FileSignature,
  UserPlus, ArrowRight, Activity, ClipboardList, Plus,
} from "lucide-react";

const greetingWord = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

// ---- formatting helpers ---------------------------------------------------
const money = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return "$" + Math.round(v).toLocaleString();
  return "$" + (v || 0);
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const timeAgo = (iso) => {
  const d = daysSince(iso);
  if (d === null) return "";
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
};

// ---- small presentational pieces ------------------------------------------
const SectionCard = ({ title, icon: Icon, action, children, testid, className = "" }) => (
  <Card className={`bg-card border-border ${className}`} data-testid={testid}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
        {Icon && <Icon size={15} className="text-muted-foreground" />} {title}
      </CardTitle>
      {action}
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

const KpiCard = ({ icon: Icon, label, value, hint, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    className="text-left rounded-xl border border-border bg-card p-4 md:p-5 transition-colors duration-150 enabled:hover:bg-muted/40 enabled:active:scale-[0.99] disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      {Icon && <Icon size={16} className="text-muted-foreground" />}
    </div>
    <div className="mt-2 text-2xl md:text-3xl font-semibold tabular-nums text-foreground">{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
  </button>
);

const EmptyRow = ({ children }) => (
  <div className="py-6 text-center text-sm text-muted-foreground">{children}</div>
);

// Fixed-height, independently scrolling content region for a dashboard widget.
// Native scrolling => mouse wheel, trackpad and keyboard (when focused) work out
// of the box. overscroll-contain (via .dash-scroll) stops the page from scrolling
// until the widget reaches its end. The thin themed scrollbar only becomes
// prominent on hover / focus / active scroll.
const ScrollList = ({ height, className = "", label, children }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let t;
    const onScroll = () => {
      el.classList.add("is-scrolling");
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove("is-scrolling"), 900);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, []);
  return (
    <div
      ref={ref}
      tabIndex={0}
      role="group"
      aria-label={label}
      style={{ height }}
      className={`dash-scroll rounded-lg pr-1.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${className}`}
    >
      {children}
    </div>
  );
};

// Per-stage accent using the established Rendeavored token palette (each hex is
// already light/dark aware via styles/theme.css). Used for the stage dot and the
// subtle share-of-pipeline progress bar on each KPI card.
const stageAccent = {
  new: "bg-[#1E6BFF]",        // Blue
  interested: "bg-[#66B2FF]", // Cyan/light-blue
  qualified: "bg-[#2EE59D]",  // Green
  meetings: "bg-[#F5B94A]",   // Amber
  closed: "bg-[#A78BFA]",     // Purple
};

export const DashboardHome = ({
  token, can, userName, onOpenProspect, onGoToProspects, onOpenCandidate, onGoToCandidates,
  onNewProspect, onNewCandidate,
}) => {
  const crmOn = can("crm.view");
  const candOn = can("candidates.view");
  const leadsOn = can("leads.view");
  const matchesOn = can("matches.view");

  const [loading, setLoading] = useState(true);
  const [aStats, setAStats] = useState(null);
  const [cStats, setCStats] = useState(null);
  const [counts, setCounts] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealTotals, setDealTotals] = useState({ total_monthly: 0, total_annual: 0 });
  const [candStats, setCandStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [subsMap, setSubsMap] = useState({});
  const [noMatchDeals, setNoMatchDeals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const jobs = [];
    if (leadsOn) {
      jobs.push(adminStats(token).then(setAStats).catch(() => {}));
    }
    if (crmOn) {
      jobs.push(crmStats(token).then((r) => { setCStats(r); setFollowUps(r.follow_ups_due || []); }).catch(() => {}));
      jobs.push(crmViewCounts(token).then((r) => setCounts(r.counts || {})).catch(() => {}));
      jobs.push(crmDeals(token).then((r) => { setDeals(r.deals || []); setDealTotals({ total_monthly: r.total_monthly || 0, total_annual: r.total_annual || 0 }); }).catch(() => {}));
      jobs.push(import("@/lib/api").then(({ crmListProspects }) =>
        crmListProspects(token, { view: "all", page: 1, page_size: 400 })
          .then((r) => setProspects(r.prospects || [])).catch(() => {})));
    }
    if (candOn) {
      jobs.push(candidateStats(token).then(setCandStats).catch(() => {}));
      jobs.push(listCandidates(token, {}).then((r) => setCandidates(r.candidates || [])).catch(() => {}));
    }
    if (leadsOn) {
      jobs.push(adminSubmissions(token, {}).then((r) => {
        const m = {};
        (r.submissions || []).forEach((s) => { m[s.id] = s; });
        setSubsMap(m);
      }).catch(() => {}));
    }
    await Promise.all(jobs);
    setLoading(false);
  }, [token, crmOn, candOn, leadsOn]);

  useEffect(() => { load(); }, [load]);

  // Secondary: detect deals with no candidate match (capped, non-blocking).
  useEffect(() => {
    if (!matchesOn || deals.length === 0) return;
    let cancelled = false;
    const targets = deals.filter((d) => d.submission_id).slice(0, 8);
    (async () => {
      const noMatch = [];
      for (const d of targets) {
        try {
          const r = await leadRecommendedCandidates(token, d.submission_id);
          if (!cancelled && (r.candidates || []).length === 0) noMatch.push(d);
        } catch { /* ignore */ }
      }
      if (!cancelled) setNoMatchDeals(noMatch);
    })();
    return () => { cancelled = true; };
  }, [deals, matchesOn, token]);

  // ---- derived data --------------------------------------------------------
  const openDeals = useMemo(
    () => deals.filter((d) => ["Draft", "Sent"].includes(d.proposal_status || "Draft")),
    [deals]
  );
  const draftDeals = useMemo(() => deals.filter((d) => (d.proposal_status || "Draft") === "Draft"), [deals]);
  const qualifiedAwaiting = useMemo(
    () => prospects.filter((p) => p.status === "Qualified" && !p.teambuilder_ai_completed),
    [prospects]
  );
  const candidatesAwaiting = useMemo(
    () => candidates.filter((c) => (c.status || "Applied") === "Applied"),
    [candidates]
  );

  // Needs Attention (ranked)
  const attention = useMemo(() => {
    const items = [];
    followUps.forEach((f) => items.push({
      key: `fu-${f.id}`, severity: "urgent", icon: Clock,
      title: `Follow up: ${f.name || f.company_name || "Prospect"}`,
      meta: `Overdue since ${f.next_follow_up_date}`, onClick: () => onOpenProspect(f.id),
    }));
    qualifiedAwaiting.forEach((p) => items.push({
      key: `qa-${p.id}`, severity: "info", icon: Sparkles,
      title: `${p.name || p.company_name} is Qualified`,
      meta: "Awaiting TeamBuilder Assessment", onClick: () => onOpenProspect(p.id),
    }));
    draftDeals.forEach((d) => items.push({
      key: `dp-${d.id}`, severity: "warning", icon: FileSignature,
      title: `${d.company || "Deal"} — proposal not generated`,
      meta: `Assessment complete · ${money(d.monthly_value)}/mo`, onClick: () => onOpenProspect(d.prospect_id),
    }));
    noMatchDeals.forEach((d) => items.push({
      key: `nm-${d.id}`, severity: "warning", icon: Users,
      title: `${d.company || "Deal"} has no candidate match`,
      meta: "Review talent pool for this Blueprint", onClick: () => onOpenProspect(d.prospect_id),
    }));
    candidatesAwaiting.forEach((c) => items.push({
      key: `cr-${c.id}`, severity: "info", icon: CircleUser,
      title: `Review candidate: ${c.name}`,
      meta: `${c.current_position || "New applicant"} · awaiting review`, onClick: () => onOpenCandidate(c.id),
    }));
    const rank = { urgent: 0, warning: 1, info: 2 };
    return items.sort((a, b) => rank[a.severity] - rank[b.severity]);
  }, [followUps, qualifiedAwaiting, draftDeals, noMatchDeals, candidatesAwaiting, onOpenProspect, onOpenCandidate]);

  // Recent Activity (merge real timestamps from existing records)
  const activity = useMemo(() => {
    const ev = [];
    prospects.forEach((p) => {
      (p.activity_log || []).forEach((a) => ev.push({
        ts: a.timestamp, icon: Activity,
        text: `${a.type}${a.outcome ? ` · ${a.outcome}` : ""} — ${p.name || p.company_name || "Prospect"}`,
        by: a.by_name, onClick: () => onOpenProspect(p.id),
      }));
    });
    deals.forEach((d) => ev.push({
      ts: d.created_at, icon: Sparkles,
      text: `Assessment generated — ${d.company || "Company"}`,
      by: d.assigned_to_name, onClick: () => onOpenProspect(d.prospect_id),
    }));
    candidates.forEach((c) => ev.push({
      ts: c.created_at, icon: UserPlus,
      text: `Candidate added — ${c.name || "Candidate"}`,
      onClick: () => onOpenCandidate(c.id),
    }));
    return ev.filter((e) => e.ts).sort((a, b) => (a.ts < b.ts ? 1 : -1)).slice(0, 14);
  }, [prospects, deals, candidates, onOpenProspect, onOpenCandidate]);

  // Recent Assessments (from Deals joined to submissions pdf_status)
  const recentAssessments = useMemo(
    () => [...deals].sort((a, b) => ((a.created_at || "") < (b.created_at || "") ? 1 : -1)).slice(0, 6),
    [deals]
  );

  // AI Suggestions (deterministic, from existing data)
  const suggestions = useMemo(() => {
    const s = [];
    if (followUps.length) s.push({
      key: "s-fu", text: `Follow up with ${followUps.length} overdue prospect${followUps.length > 1 ? "s" : ""}.`,
      why: "These follow-up dates have passed.", cta: "Open follow-ups", onClick: () => onGoToProspects("follow_ups"),
    });
    if (qualifiedAwaiting.length) s.push({
      key: "s-qa", text: `Launch assessments for ${qualifiedAwaiting.length} Qualified prospect${qualifiedAwaiting.length > 1 ? "s" : ""}.`,
      why: "Qualified but no TeamBuilder Assessment yet.", cta: "View Qualified", onClick: () => onGoToProspects("qualified"),
    });
    if (draftDeals.length) s.push({
      key: "s-dp", text: `Generate proposals for ${draftDeals.length} completed assessment${draftDeals.length > 1 ? "s" : ""}.`,
      why: "Assessment done, proposal still in Draft.", cta: "Open prospect",
      onClick: () => onOpenProspect(draftDeals[0].prospect_id),
    });
    noMatchDeals.forEach((d) => s.push({
      key: `s-nm-${d.id}`, text: `${d.company || "A deal"} has no matched candidates.`,
      why: "No candidates match this Blueprint yet.", cta: "View matches", onClick: () => onOpenProspect(d.prospect_id),
    }));
    if (candidatesAwaiting.length) s.push({
      key: "s-cr", text: `Review ${candidatesAwaiting.length} new candidate${candidatesAwaiting.length > 1 ? "s" : ""}.`,
      why: "Newly applied, not yet reviewed.", cta: "Open candidates", onClick: () => onGoToCandidates(),
    });
    return s.slice(0, 6);
  }, [followUps, qualifiedAwaiting, draftDeals, noMatchDeals, candidatesAwaiting, onGoToProspects, onOpenProspect, onGoToCandidates]);

  // ---- render --------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <Skeleton className="h-80 rounded-xl xl:col-span-7" />
          <Skeleton className="h-80 rounded-xl xl:col-span-5" />
        </div>
      </div>
    );
  }

  const pipeline = [
    ["new", "New"], ["interested", "Interested"], ["qualified", "Qualified"],
    ["meetings", "Meetings"], ["closed", "Closed"],
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Greeting + quick actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            {greetingWord()}{userName ? `, ${String(userName).split(" ")[0]}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">TeamBuilder AI Administration</p>
        </div>
        <div className="flex items-center gap-2">
          {can("crm.manage") && (
            <Button onClick={onNewProspect} data-testid="home-new-prospect-button"
              className="h-10 bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={16} className="mr-1.5" /> New Prospect
            </Button>
          )}
          {can("candidates.view") && (
            <Button onClick={onNewCandidate} variant="outline" data-testid="home-new-candidate-button"
              className="h-10 border-border text-foreground hover:bg-muted/60">
              <UserPlus size={16} className="mr-1.5" /> New Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Row 1 — KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {crmOn && (
          <KpiCard icon={Target} label="Prospects" value={(cStats?.total ?? counts?.all ?? 0).toLocaleString()}
            hint="Total in pipeline" onClick={() => onGoToProspects("all")} testid="kpi-prospects" />
        )}
        {candOn && (
          <KpiCard icon={Users} label="Candidates" value={(candStats?.total ?? 0).toLocaleString()}
            hint="Talent pool" onClick={() => onGoToCandidates()} testid="kpi-candidates" />
        )}
        {crmOn && (
          <KpiCard icon={Briefcase} label="Open Deals" value={openDeals.length.toLocaleString()}
            hint="Draft or Sent" onClick={() => onGoToProspects("all")} testid="kpi-open-deals" />
        )}
        {leadsOn && (
          <KpiCard icon={FileText} label="Assessments" value={(aStats?.total_submissions ?? 0).toLocaleString()}
            hint="Blueprints generated" testid="kpi-assessments" />
        )}
      </div>

      {/* Row 2 — Sales Pipeline (compact, scannable KPI cards per stage) */}
      {crmOn && counts && (
        <SectionCard title="Sales Pipeline" icon={TrendingUp} testid="home-pipeline-card"
          action={<Button variant="ghost" size="sm" className="text-muted-foreground h-8" onClick={() => onGoToProspects("all")}>All prospects <ChevronRight size={14} /></Button>}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {pipeline.map(([key, label]) => {
              const val = counts[key] ?? 0;
              const total = Math.max(
                1,
                counts.all ?? pipeline.reduce((s, [k]) => s + (counts[k] ?? 0), 0),
              );
              const share = (val / total) * 100;
              const shareLabel = share === 0 ? "0%" : `${share.toFixed(1).replace(/\.0$/, "")}%`;
              const accent = stageAccent[key] || "bg-muted-foreground";
              return (
                <button key={key} type="button" onClick={() => onGoToProspects(key)}
                  data-testid="pipeline-stage-tile"
                  aria-label={`${label}: ${val} prospects, ${shareLabel} of pipeline. Filter prospects.`}
                  className="group text-left rounded-xl border border-border bg-card p-4 transition-colors duration-150 hover:bg-muted/40 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${accent}`} />
                    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground truncate">{label}</span>
                  </div>
                  <div className="mt-2 text-3xl font-semibold tabular-nums text-foreground leading-none">
                    {val.toLocaleString()}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground tabular-nums">{shareLabel}</div>
                  <div className="mt-2 h-1.5 rounded-full bg-muted/50 overflow-hidden" aria-hidden="true">
                    <div className={`h-full rounded-full ${accent} transition-[width] duration-300`}
                      style={{ width: `${Math.max(share > 0 ? 6 : 0, Math.min(100, share))}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Row 3 — Revenue Snapshot */}
      {(crmOn || leadsOn) && (
        <SectionCard title="Revenue Snapshot" icon={DollarSign} testid="home-revenue-card">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <RevenueTile icon={DollarSign} label="Monthly Pipeline" value={money(dealTotals.total_monthly)}
              onClick={() => onGoToProspects("all")} testid="revenue-monthly" />
            <RevenueTile icon={TrendingUp} label="Annual Pipeline" value={money(dealTotals.total_annual)}
              onClick={() => onGoToProspects("all")} testid="revenue-annual" />
            {leadsOn && (
              <RevenueTile icon={Trophy} label="Won Revenue" value={money(aStats?.won_value)}
                accent testid="revenue-won" />
            )}
            {leadsOn && (
              <RevenueTile icon={Percent} label="Conversion" value={`${aStats?.conversion_rate ?? 0}%`}
                testid="revenue-conversion" />
            )}
          </div>
        </SectionCard>
      )}

      {/* Tier 1/2 command layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* LEFT: Needs Attention + AI Suggestions */}
        <div className="xl:col-span-7 space-y-6">
          <SectionCard title="Needs Attention" icon={AlertTriangle} testid="home-needs-attention-card"
            action={<Badge className="bg-destructive/15 text-destructive border border-destructive/30 tabular-nums">{attention.length}</Badge>}>
            {attention.length === 0 ? (
              <EmptyRow>All clear. No overdue follow-ups or blocked deals.</EmptyRow>
            ) : (
              <ScrollList height="360px" label="Needs attention list">
                <div className="space-y-1">
                  {attention.map((it) => (
                    <button key={it.key} type="button" onClick={it.onClick} data-testid="needs-attention-row"
                      className="group w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${it.severity === "urgent" ? "bg-destructive" : it.severity === "warning" ? "bg-[hsl(var(--chart-4))]" : "bg-[hsl(var(--chart-2))]"}`} />
                      <it.icon size={15} className="shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-foreground">{it.title}</div>
                        <div className="truncate text-xs text-muted-foreground">{it.meta}</div>
                      </div>
                      <ChevronRight size={15} className="shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" />
                    </button>
                  ))}
                </div>
              </ScrollList>
            )}
          </SectionCard>

          <SectionCard title="AI Suggestions" icon={Sparkles} testid="home-suggestions-card">
            {suggestions.length === 0 ? (
              <EmptyRow>Nothing suggested right now — you're on top of things.</EmptyRow>
            ) : (
              <ScrollList height="220px" label="AI suggestions list">
                <div className="space-y-2">
                  {suggestions.map((s) => (
                    <div key={s.key} data-testid="ai-suggestion-item"
                      className="flex items-start gap-3 rounded-lg border border-border bg-muted/20 p-3">
                      <ClipboardList size={16} className="mt-0.5 shrink-0 text-[hsl(var(--chart-3))]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-foreground">{s.text}</div>
                        <div className="text-xs text-muted-foreground">{s.why}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 shrink-0 border-border" onClick={s.onClick}>
                        {s.cta} <ArrowRight size={13} className="ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollList>
            )}
          </SectionCard>
        </div>

        {/* RIGHT: Recent Activity */}
        <div className="xl:col-span-5 space-y-6">
          <SectionCard title="Recent Activity" icon={Activity} testid="home-activity-card">
            {activity.length === 0 ? (
              <EmptyRow>No recent activity yet.</EmptyRow>
            ) : (
              <ScrollList height="620px" label="Recent activity list">
                <div className="space-y-1">
                  {activity.map((e, i) => (
                    <button key={i} type="button" onClick={e.onClick} data-testid="recent-activity-item"
                      className="w-full flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <e.icon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-foreground">{e.text}</div>
                        <div className="text-xs text-muted-foreground">{timeAgo(e.ts)}{e.by ? ` · ${e.by}` : ""}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollList>
            )}
          </SectionCard>
        </div>
      </div>

      {/* Row 6 — Candidate Snapshot + Row 7 — Recent Assessments */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {candOn && (
          <SectionCard title="Candidate Snapshot" icon={Users} className="xl:col-span-5" testid="home-candidates-card"
            action={<Button variant="ghost" size="sm" className="text-muted-foreground h-8" onClick={() => onGoToCandidates()}>View all <ChevronRight size={14} /></Button>}>
            {candStats && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[["Available", candStats.available], ["Interviewing", candStats.interviewing], ["Placed", candStats.placed], ["New (7d)", candStats.new_this_week]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border bg-muted/20 p-2 text-center">
                    <div className="text-lg font-semibold tabular-nums text-foreground">{v ?? 0}</div>
                    <div className="text-[10px] text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            )}
            {candidates.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-3">No candidates yet.</p>
                <Button size="sm" variant="outline" className="border-border" onClick={onNewCandidate}>Add candidate</Button>
              </div>
            ) : (
              <ScrollList height="260px" label="Candidate snapshot list">
                <div className="divide-y divide-border/60">
                  {candidates.slice(0, 6).map((c) => (
                    <button key={c.id} type="button" onClick={() => onOpenCandidate(c.id)} data-testid="candidate-snapshot-row"
                      className="w-full flex items-center gap-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted/30 rounded-md px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-foreground">{c.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{c.current_position || "—"}</div>
                      </div>
                      <Badge className="bg-muted text-muted-foreground border border-border shrink-0">{c.status || "Applied"}</Badge>
                      <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">—</span>
                    </button>
                  ))}
                </div>
              </ScrollList>
            )}
          </SectionCard>
        )}

        {leadsOn && (
          <SectionCard title="Recent Assessments" icon={FileText} className="xl:col-span-7" testid="home-assessments-card">
            {recentAssessments.length === 0 ? (
              <EmptyRow>No assessments generated yet. Qualify a prospect to launch one.</EmptyRow>
            ) : (
              <ScrollList height="340px" className="scroll-x" label="Recent assessments table">
                <Table>
                  <TableHeader className="dash-sticky-head">
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Company</TableHead>
                      <TableHead className="text-muted-foreground">Role</TableHead>
                      <TableHead className="text-muted-foreground">Monthly</TableHead>
                      <TableHead className="text-muted-foreground">Blueprint</TableHead>
                      <TableHead className="text-muted-foreground">Generated</TableHead>
                      <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentAssessments.map((d) => {
                      const sub = subsMap[d.submission_id];
                      const bp = sub?.pdf_status;
                      const role = (d.roles && d.roles[0]) ? `${d.roles[0].name}${d.roles.length > 1 ? ` +${d.roles.length - 1}` : ""}` : "—";
                      return (
                        <TableRow key={d.id} className="border-border" data-testid="recent-assessment-row">
                          <TableCell className="text-foreground font-medium">{d.company || "—"}</TableCell>
                          <TableCell className="text-muted-foreground max-w-[160px] truncate">{role}</TableCell>
                          <TableCell className="tabular-nums text-[hsl(var(--chart-3))]">{money(d.monthly_value)}</TableCell>
                          <TableCell>
                            <Badge className={bp === "ready"
                              ? "bg-[hsl(var(--chart-3))]/15 text-foreground border border-[hsl(var(--chart-3))]/30"
                              : "bg-muted text-muted-foreground border border-border"}>
                              {bp === "ready" ? "Ready" : (bp ? bp : "—")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{(d.created_at || "").slice(0, 10) || "—"}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {d.submission_id && (
                              <Button size="sm" variant="ghost" className="h-7 text-primary" data-testid="assessment-open-blueprint"
                                onClick={() => window.open(`/blueprint/${d.submission_id}`, "_blank")}>Blueprint</Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" data-testid="assessment-open-prospect"
                              onClick={() => onOpenProspect(d.prospect_id)}>Prospect</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollList>
            )}
          </SectionCard>
        )}
      </div>
    </div>
  );
};

const RevenueTile = ({ icon: Icon, label, value, accent, onClick, testid }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
    className="text-left rounded-lg border border-border bg-muted/20 p-4 transition-colors duration-150 enabled:hover:bg-muted/40 enabled:active:scale-[0.99] disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon size={15} /><span className="text-xs">{label}</span>
    </div>
    <div className={`mt-2 text-3xl md:text-4xl font-semibold tabular-nums ${accent ? "text-[hsl(var(--chart-3))]" : "text-foreground"}`}>{value}</div>
  </button>
);
